import { GetStaticPropsContext, InferGetStaticPropsType, NextPage } from 'next';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { useIntl } from 'react-intl';

import { urlFor } from 'utils/routes.utils';
import { notEmpty } from 'utils/typescript.utils';

import { Duration } from 'constants/duration.constants';
import { Routes } from 'constants/routes.constants';
import { seoTitleSuffixQuery } from 'queries/global/queries';
import Prefetcher from 'services/prefetcher';

import CalendarContainer from 'components/@calendar/CalendarContainer';
import AppNavigation from 'components/@navigation/AppNavigation';

const EventsCalendar: NextPage<
  InferGetStaticPropsType<typeof getStaticProps>
> = ({ seoTitleSuffix, anchorMonth }) => {
  const intl = useIntl();
  const { locale, defaultLocale } = useRouter();

  const title = [
    intl.formatMessage({ id: 'seo.events.calendar.title' }),
    seoTitleSuffix,
  ]
    .filter(notEmpty)
    .join('');

  // Fixed, so the ?day= param never turns each day into its own canonical.
  const canonical =
    locale === defaultLocale
      ? urlFor(Routes.EventsCalendar)
      : urlFor(`/${locale}${Routes.EventsCalendar}`);

  return (
    <AppNavigation>
      <NextSeo title={title} canonical={canonical} />
      <CalendarContainer anchorMonth={anchorMonth} />
    </AppNavigation>
  );
};

export default EventsCalendar;

export const getStaticProps = async ({
  locale,
  preview,
}: GetStaticPropsContext) => {
  const prefetcher = await Prefetcher.init({ locale, preview });
  const { seoTitleSuffix } = await prefetcher.prefetch(seoTitleSuffixQuery);

  // Resolved here rather than on the client, so the month the server renders and
  // the month it hydrates into are the same one.
  const now = new Date();
  const anchorMonth = `${now.getUTCFullYear()}-${String(
    now.getUTCMonth() + 1,
  ).padStart(2, '0')}`;

  return {
    props: {
      seoTitleSuffix,
      anchorMonth,
      dehydratedState: prefetcher.dehydratedState,
    },
    revalidate: Duration.TEN_MIN,
  };
};
